import type { CSSObject } from "type/tss";
import { Theme } from "@mui/material/styles";

export const styles = (theme: Theme) => {
	const { custom } = theme.palette;
	// The underline and focus ring follow a reseller's own colour; secondary falls back to the
	// brand orange when none is set.
	const accent = theme.palette.secondary.main;
	return ({
		root: {
			minHeight: 0,
			borderBottom: `1px solid ${custom.divider}`,
			marginBottom: 16,
		},
		// The row is only as wide as its tabs, so the gray rule runs under the tabs alone.
		// MUI clips the root and scroller (overflow: hidden) for its sliding indicator; the
		// underline here is each tab's own bottom border pulled 1px over the row's rule, and
		// the focus outline sits 4px outside the tab, so both would be clipped.
		fixedRoot: {
			width: "fit-content",
			overflow: "visible",
			// MUI sets overflow: hidden as an inline style on the scroller of a row that does
			// not scroll, so no class can override it without !important.
			"& .MuiTabs-scroller": {
				overflow: "visible !important",
			},
		},
		// A row that scrolls has to clip, so its underline is drawn inside the tab instead of
		// over the row's rule.
		scrollableRoot: {
			"& .MuiTab-root": {
				marginBottom: 0,
			},
		},
		flexContainer: {
			gap: 0,
		},
		indicator: {
			display: "none",
		},
		tab: {
			minWidth: 0,
			minHeight: 0,
			maxWidth: "none",
			overflow: "visible",
			padding: "0 16px 8px",
			background: "none",
			border: "none",
			borderBottom: "2px solid transparent",
			marginBottom: -1,
			cursor: "pointer",
			fontFamily: theme.typography.fontFamily,
			fontSize: 16,
			lineHeight: "24px",
			letterSpacing: "normal",
			textTransform: "none",
			fontWeight: 400,
			color: custom.textSecondary,
			opacity: 1,
			transition: "color 120ms",
			"&:hover": {
				color: custom.textPrimary,
			},
			"&.Mui-selected": {
				fontWeight: 700,
				color: custom.textPrimary,
				borderBottomColor: accent,
			},
			// Mui-focusVisible is MUI's keyboard-only focus class, so a mouse click draws no outline.
			"&.Mui-focusVisible, &:focus-visible": {
				outline: `2px solid ${accent}`,
				outlineOffset: 4,
				borderRadius: 2,
			},
		},
		label: {
			display: "flex",
			alignItems: "center",
			gap: 8,
		},
		// A hidden bold copy of the text shares the grid cell, so each tab is always as wide as
		// its bold (selected) form and switching tabs never moves its neighbour.
		text: {
			display: "inline-grid",
			"& > span, &::after": {
				gridArea: "1 / 1",
			},
			"&::after": {
				content: "attr(data-label)",
				fontWeight: 700,
				visibility: "hidden",
				height: 0,
				overflow: "hidden",
			},
		},
	}) satisfies Record<string, CSSObject>;
};
